import { ReactNode, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useMsal } from '@azure/msal-react'
import {
  LayoutDashboard,
  FolderKanban,
  FileText,
  MessageSquare,
  BarChart3,
  FileSpreadsheet,
  Grid3X3,
  CheckSquare,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronDown,
  Hammer,
} from 'lucide-react'
import GeekModeToggle from './GeekModeToggle'

interface LayoutProps {
  children: ReactNode
}

const navigation = [
  { name: 'Dashboard', href: '/', icon: LayoutDashboard },
  { name: 'Projects', href: '/projects', icon: FolderKanban },
]

const projectNavigation = [
  { name: 'Documents', href: '/documents', icon: FileText },
  { name: 'Chat', href: '/chat', icon: MessageSquare },
  { name: 'Analysis', href: '/analysis', icon: BarChart3 },
  { name: 'Outputs', href: '/outputs', icon: FileSpreadsheet },
  { name: 'Smartsheets', href: '/smartsheets', icon: Grid3X3 },
  { name: 'Post-Import', href: '/post-import', icon: CheckSquare },
]

export default function Layout({ children }: LayoutProps) {
  const location = useLocation()
  const { instance, accounts } = useMsal()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)

  const currentUser = accounts[0]

  // Check if we're in a project context
  const projectMatch = location.pathname.match(/\/projects\/([^/]+)/)
  const projectId = projectMatch?.[1]

  const handleLogout = () => {
    instance.logoutRedirect()
  }

  const isActiveLink = (href: string) => {
    if (href === '/') return location.pathname === '/'
    return location.pathname.startsWith(href)
  }

  const isActiveProjectLink = (href: string) => {
    if (!projectId) return false
    return location.pathname === `/projects/${projectId}${href}`
  }

  return (
    <div className="min-h-screen bg-gradient-dark">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-slate-900/95 border-r border-slate-700/50 transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-4 border-b border-slate-700/50">
            <Link to="/" className="flex items-center space-x-3">
              <div className="w-9 h-9 bg-gradient-to-br from-orange-500 to-amber-500 rounded-lg flex items-center justify-center shadow-lg shadow-orange-500/25">
                <Hammer className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold gradient-text">SprintForge</span>
            </Link>
            <button
              className="lg:hidden p-2 rounded-md text-slate-400 hover:bg-slate-800/50 hover:text-slate-200"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Main Navigation */}
          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto scrollbar-thin">
            {navigation.map((item) => {
              const isActive = isActiveLink(item.href)
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={isActive ? 'sidebar-link-active' : 'sidebar-link'}
                  onClick={() => setSidebarOpen(false)}
                >
                  <item.icon className="w-5 h-5" />
                  {item.name}
                </Link>
              )
            })}

            {/* Project Navigation - shown when in project context */}
            {projectId && (
              <>
                <div className="pt-4 pb-2 px-4">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Project
                  </p>
                </div>
                {projectNavigation.map((item) => {
                  const isActive = isActiveProjectLink(item.href)
                  return (
                    <Link
                      key={item.name}
                      to={`/projects/${projectId}${item.href}`}
                      className={isActive ? 'sidebar-link-active' : 'sidebar-link'}
                      onClick={() => setSidebarOpen(false)}
                    >
                      <item.icon className="w-5 h-5" />
                      {item.name}
                    </Link>
                  )
                })}
              </>
            )}

            {/* Settings - at the bottom of main nav */}
            <div className="pt-4 border-t border-slate-700/50 mt-4">
              <Link
                to="/settings"
                className={isActiveLink('/settings') ? 'sidebar-link-active' : 'sidebar-link'}
                onClick={() => setSidebarOpen(false)}
              >
                <Settings className="w-5 h-5" />
                Settings
              </Link>
            </div>
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-slate-700/50">
            <div className="relative">
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="w-full flex items-center px-3 py-2.5 rounded-lg hover:bg-slate-800/50 transition-colors"
              >
                <div className="w-9 h-9 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center shadow-lg shadow-purple-500/25">
                  <span className="text-white font-semibold text-sm">
                    {currentUser?.name?.charAt(0).toUpperCase() || 'U'}
                  </span>
                </div>
                <div className="ml-3 flex-1 text-left min-w-0">
                  <p className="text-sm font-medium text-slate-200 truncate">
                    {currentUser?.name || 'User'}
                  </p>
                  <p className="text-xs text-slate-500 truncate">{currentUser?.username || ''}</p>
                </div>
                <ChevronDown
                  className={`w-4 h-4 text-slate-500 transition-transform ${
                    userMenuOpen ? 'rotate-180' : ''
                  }`}
                />
              </button>

              {userMenuOpen && (
                <div className="absolute bottom-full left-0 right-0 mb-2 glass-panel py-1 shadow-xl animate-slideUp">
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center px-4 py-2 text-sm text-red-400 hover:bg-red-500/10 hover:text-red-300 transition-colors"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <header className="sticky top-0 z-30 bg-slate-900/80 backdrop-blur-xl border-b border-slate-700/50">
          <div className="flex items-center justify-between h-16 px-4">
            <button
              className="lg:hidden p-2 rounded-lg text-slate-400 hover:bg-slate-800/50 hover:text-slate-200 transition-colors"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="w-5 h-5" />
            </button>
            <div className="flex-1" />
            {/* Geek Mode Toggle */}
            <GeekModeToggle />
          </div>
        </header>

        {/* Page content */}
        <main className="p-6">{children}</main>
      </div>
    </div>
  )
}
